import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import { API } from "./api";
const AuthContext = createContext(null);

const bearer = () => {
  const t = localStorage.getItem("rada_session");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Processes the OAuth session_id from the URL fragment exactly once.
const AuthCallback = ({ onDone }) => {
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = window.location.hash.split("session_id=")[1]?.split("&")[0];
    // Clean the URL first so a re-render can't loop.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (!sid) { onDone(null, true); return; }
    axios
      .post(`${API}/auth/session`, {}, { headers: { "X-Session-ID": sid }, withCredentials: true })
      .then((r) => {
        if (r.data?.session_token) localStorage.setItem("rada_session", r.data.session_token);
        onDone(r.data, null);
      })
      .catch(() => onDone(null, true));
  }, [onDone]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#1F3864]">
      <div className="text-[#EBE6E0] font-bold animate-pulse">Signing you in…</div>
    </div>
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(() => window.location.hash?.includes("session_id="));
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true, headers: bearer() });
      setUser(data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // If returning from OAuth, AuthCallback exchanges the session first — skip the /me check.
    if (processing) return;
    checkAuth();
  }, [checkAuth, processing]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers: bearer() });
    } catch {}
    localStorage.removeItem("rada_session");
    setUser(null);
  };

  const onCallbackDone = useCallback((u, err) => {
    setProcessing(false);
    setUser(u);
    setLoading(false);
    if (err) {
      window.history.replaceState(null, "", "/");
      return;
    }
    // Admins go to the admin dashboard after sign-in; others honor any pre-set destination.
    if (u?.is_admin || u?.role === "admin") {
      sessionStorage.removeItem("rada_auth_redirect");
      window.location.href = "/admin";
      return;
    }
    const dest = sessionStorage.getItem("rada_auth_redirect");
    sessionStorage.removeItem("rada_auth_redirect");
    if (dest === "/admin") {
      window.location.href = "/admin";
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {processing ? <AuthCallback onDone={onCallbackDone} /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
